import { PaginatedList } from 'helpers/status'

import {
  EventPointOfInterest,
  GenericPointOfInterest,
  StopPointOfInterest,
  Stop,
  Tour,
  TourCategory,
  CarouselItem,
  WayfindingMap,
  Mood,
  VisitorType
} from 'generated/mos/org/getty/audio'
import { BundleSummary, Release, Bundle } from 'generated/mos/org/getty/publish'
import { ValidationFailure } from 'generated/mos/org/getty/validation'
import { CreateReleaseResponse } from 'generated/mos/org/getty/publishmanagement'
import { VisitorInfoPage, VisitorInfoSiteContent, Amenity } from 'generated/mos/org/getty/visitorinfo'
import { TMSObject } from 'generated/mos/org/getty/integrationtms'
import { GetLinkedFieldMappingsResponse } from 'generated/mos/linkedfieldmappings'
import { ResolvedValueResult } from 'generated/mos/linkedfieldresolver'
import { BuildingLevel, Site, Space } from 'generated/mos/structure';

import { DataStatus as GenericDataStatus, EntityStatus as GenericEntityStatus, Status } from 'helpers/status'
import { Media } from 'generated/mos/media'
import { Getty360Event } from "../../../generated/mos/org/getty/integration";

type GettyErrorCode = string;

export type DataStatus<T> = GenericDataStatus<T, GettyErrorCode>;
export type EntityStatus<T extends object> = GenericEntityStatus<T, GettyErrorCode, string>;

export type TourWithStops = Tour.Entity & { stops?: ReadonlyArray<Stop.Entity> };
export type StopWithSpace = Stop.Entity & { space?: Space.Entity };
export type StopList = PaginatedList<StopWithSpace>;

export type TourList = ReadonlyArray<Tour.Entity>;
export type MoodList = ReadonlyArray<Mood.Entity>;
export type VisitorTypeList = ReadonlyArray<VisitorType.Entity>;

export type CategoriesList = { tourCategories: ReadonlyArray<TourCategory.Entity>; tours: ReadonlyArray<Tour.Entity> };

export type CarouselItems = {
  readonly carouselItems: ReadonlyArray<CarouselItem.Entity>;
  readonly mediaList?: ReadonlyArray<Media.Entity>;
};

export type TmsObjectList = PaginatedList<TMSObject.Entity>;

type UpdateStop = {
  readonly baseEntity?: TMSObject.Entity;
  readonly fieldMappings?: ReadonlyArray<GetLinkedFieldMappingsResponse.Mapping.Entity>;
  readonly relatedStops?: ReadonlyArray<Stop.Entity>;
  readonly resolvedValues?: ReadonlyArray<ResolvedValueResult.Entity>;
  readonly stop: Stop.Entity;
  readonly tours?: ReadonlyArray<Tour.Entity>;
  readonly moods?: ReadonlyArray<Mood.Entity>;
}

type DeleteStop = {
  readonly tours: ReadonlyArray<Tour.Entity>;
}

type UpdateTour = {
  readonly tour?: TourWithStops;
}

export type MoodWithStopPointsOfInterest = Mood.Entity & {
  stopPointsOfInterest?: ReadonlyArray<StopPointOfInterest.Entity>;
  genericPointsOfInterest?: ReadonlyArray<GenericPointOfInterest.Entity>;
  eventPointsOfInterest?: ReadonlyArray<EventPointOfInterest.Entity>;
};

type UpdateMood = {
  readonly mood?: MoodWithStopPointsOfInterest;
}

type UpdateVisitorType = {
  readonly visitorType?: VisitorType.Entity;
}

type Sites = {
  readonly sites?: ReadonlyArray<Site.Entity>;
}

type BundleSummaries = ReadonlyArray<BundleSummary.Entity>
type Releases = ReadonlyArray<Release.Entity>

export type VisitorInfoPages = {
  readonly siteContentRef: VisitorInfoSiteContent.Ref;
  readonly pages: ReadonlyArray<{
    readonly page: VisitorInfoPage.Entity;
    readonly amenities: ReadonlyArray<Amenity.Entity> | undefined;
  }>;
}

export type AmentityUpdate = {
  readonly amenity: Amenity.Entity;
  readonly wayfindingMaps?: ReadonlyArray<WayfindingMap.Entity>;
}

export type AnalyticsResponse = {
  readonly session?: { dashboardUrl: string } | undefined;
  readonly stops?: { dashboardUrl: string } | undefined;
  readonly tours?: { dashboardUrl: string } | undefined;
}

export type ListAllValidationFailures = {
  failures: ReadonlyArray<ValidationFailure.Entity>;
  summaries: ReadonlyArray<BundleSummary.Entity>;
}

type Events = {
  readonly getty360Events?: ReadonlyArray<Getty360Event.Entity>;
}

type UpdateStopPointOfInterest = {
  readonly resolvedValues?: ReadonlyArray<ResolvedValueResult.Entity>;
  readonly baseEntity?: any;
  readonly stopPointOfInterest: StopPointOfInterest.Entity;
  readonly moods?: ReadonlyArray<Mood.Entity>;
  readonly tours?: ReadonlyArray<Tour.Entity>;
}

type UpdateEventPointOfInterest = {
  readonly resolvedValues?: ReadonlyArray<ResolvedValueResult.Entity>;
  readonly eventPointOfInterest: EventPointOfInterest.Entity;
}

type UpdateGenericPointOfInterest = {
  readonly genericPointOfInterest: GenericPointOfInterest.Entity;
}

export type GettyAppState = {
  readonly stopCreate: DataStatus<Stop.Entity>;
  readonly stopDelete: EntityStatus<DeleteStop>;
  readonly stopUpdate: DataStatus<UpdateStop>;
  readonly stops: DataStatus<StopList>;
  readonly sites: DataStatus<Sites>;
  readonly tourCreate: DataStatus<Tour.Ref>;
  readonly tourUpdate: DataStatus<UpdateTour>;
  readonly tourDelete: EntityStatus<Tour.Entity>;
  readonly tours: DataStatus<TourList>;
  readonly moodCreate: DataStatus<Mood.Ref>;
  readonly moodUpdate: DataStatus<UpdateMood>;
  readonly moodDelete: EntityStatus<Mood.Entity>;
  readonly moods: DataStatus<MoodList>;
  readonly visitorTypeCreate: DataStatus<VisitorType.Entity>;
  readonly visitorTypeUpdate: DataStatus<UpdateVisitorType>;
  readonly visitorTypeDelete: EntityStatus<VisitorType.Entity>;
  readonly visitorTypes: DataStatus<VisitorTypeList>;
  readonly categoryCreate: DataStatus<TourCategory.Ref>;
  readonly categoryDelete: DataStatus<TourCategory.Ref>;
  readonly categoryUpdate: DataStatus<TourCategory.Entity>;
  readonly categories: DataStatus<CategoriesList>;
  readonly carouselItemCreate: DataStatus<CarouselItem.Entity>;
  readonly carouselItemDelete: DataStatus<CarouselItem.Ref>;
  readonly carouselItemUpdate: DataStatus<CarouselItem.Entity>;
  readonly carouselItems: DataStatus<CarouselItems>;
  readonly tmsObjectList: DataStatus<TmsObjectList>;
  readonly visitorInfo: DataStatus<VisitorInfoPages>;
  readonly visitorInfoUpdate: DataStatus<VisitorInfoPage.Entity>;
  readonly visitorInfoCreate: DataStatus<VisitorInfoPage.Entity>;
  readonly visitorInfoDelete: DataStatus<VisitorInfoPage.Ref>;
  readonly amenityDelete: DataStatus<Amenity.Ref>;
  readonly amenityCreate: DataStatus<Amenity.Entity>;
  readonly amenityUpdate: DataStatus<AmentityUpdate>;
  readonly analytics: DataStatus<AnalyticsResponse>;
  readonly publishReleases: DataStatus<Releases>;
  readonly publishBundleSummaries: DataStatus<BundleSummaries>;
  readonly publishReleaseCreate: DataStatus<CreateReleaseResponse.Entity>;
  readonly publishBundlesList: DataStatus<ReadonlyArray<Bundle.Entity>>;
  readonly validationFailuresList: DataStatus<ReadonlyArray<ValidationFailure.Entity>>;
  readonly allValidationFailuresList: DataStatus<ListAllValidationFailures>;
  readonly events: DataStatus<Events>;
  readonly stopPointOfInterestCreate: DataStatus<StopPointOfInterest.Entity>;
  readonly stopPointOfInterestUpdate: DataStatus<UpdateStopPointOfInterest>;
  readonly stopPointOfInterestDelete: EntityStatus<StopPointOfInterest.Entity>;
  readonly eventPointOfInterestCreate: DataStatus<EventPointOfInterest.Entity>;
  readonly eventPointOfInterestUpdate: DataStatus<UpdateEventPointOfInterest>;
  readonly eventPointOfInterestDelete: EntityStatus<EventPointOfInterest.Entity>;
  readonly genericPointOfInterestCreate: DataStatus<GenericPointOfInterest.Entity>;
  readonly genericPointOfInterestUpdate: DataStatus<UpdateGenericPointOfInterest>;
  readonly genericPointOfInterestDelete: EntityStatus<GenericPointOfInterest.Entity>;
};

export const initialGettyAppState: GettyAppState = {
  stopCreate: { status: Status.Idle },
  stopDelete: { status: Status.Idle },
  stopUpdate: { status: Status.Idle },
  sites: { status: Status.Idle },
  stops: { status: Status.Idle },
  tourCreate: { status: Status.Idle },
  tourUpdate: { status: Status.Idle },
  tourDelete: { status: Status.Idle },
  moodCreate: { status: Status.Idle },
  moodUpdate: { status: Status.Idle },
  moodDelete: { status: Status.Idle },
  categoryCreate: { status: Status.Idle },
  categoryDelete: { status: Status.Idle },
  categoryUpdate: { status: Status.Idle },
  categories: { status: Status.Idle },
  tours: { status: Status.Idle },
  moods: { status: Status.Idle },
  visitorTypes: { status: Status.Idle },
  visitorTypeCreate: { status: Status.Idle },
  visitorTypeUpdate: { status: Status.Idle },
  visitorTypeDelete: { status: Status.Idle },
  carouselItemCreate: { status: Status.Idle },
  carouselItemDelete: { status: Status.Idle },
  carouselItemUpdate: { status: Status.Idle },
  carouselItems: { status: Status.Idle },
  tmsObjectList: { status: Status.Idle },
  visitorInfo: { status: Status.Idle },
  visitorInfoUpdate: { status: Status.Idle },
  visitorInfoCreate: { status: Status.Idle },
  visitorInfoDelete: { status: Status.Idle },
  amenityDelete: { status: Status.Idle },
  amenityCreate: { status: Status.Idle },
  amenityUpdate: { status: Status.Idle },
  analytics: { status: Status.Idle },
  publishReleases: { status: Status.Idle },
  publishBundleSummaries:{ status: Status.Idle },
  publishReleaseCreate: { status: Status.Idle },
  publishBundlesList: { status: Status.Idle },
  validationFailuresList: { status: Status.Idle },
  allValidationFailuresList: { status: Status.Idle },
  events: { status: Status.Idle },
  stopPointOfInterestCreate: { status: Status.Idle },
  stopPointOfInterestUpdate: { status: Status.Idle },
  stopPointOfInterestDelete: { status: Status.Idle },
  eventPointOfInterestCreate: { status: Status.Idle },
  eventPointOfInterestUpdate: { status: Status.Idle },
  eventPointOfInterestDelete: { status: Status.Idle },
  genericPointOfInterestCreate: { status: Status.Idle },
  genericPointOfInterestUpdate: { status: Status.Idle },
  genericPointOfInterestDelete: { status: Status.Idle },
}

export type GettyStore = {
  readonly getty: GettyAppState;
}
